/**
 * PM Message Correction — correction modal with a live word-level diff preview.
 *
 * A correction is sent through the regular send-message endpoint with
 * correct_msg_id / correction_comment params; the server renders the
 * correction bubble (pm/partials/message-body.twig) and the response HTML is
 * appended like any sent message.
 *
 * The diff is modules/text-diff.js, which mirrors Interpals\Helpers\TextDiff,
 * so the editor preview matches the server-rendered bubble. Known divergence:
 * the server also enforces a per-render aggregate cell budget across all
 * corrections on the page, so on a page dense with large corrections the sent
 * bubble can degrade to a whole-line replacement even though the preview
 * showed a word-level diff.
 */

import {createTextCompositionTracker} from '../../modules/text-composition-tracker.js';
import {DIFF_DELETE_CLASSES, DIFF_INSERT_CLASSES, textDiff} from '../../modules/text-diff.js';

(function () {
  window.pmTextDiff = textDiff;

  /**
   * Fill a preview line with diff segments. keepOp selects which changed op
   * this line shows ('del' for the original line, 'ins' for the corrected
   * line); the opposite op is skipped, 'eq' text renders plain.
   */
  function fillDiffLine(el, segments, keepOp, labels) {
    if (!el) return;
    el.textContent = '';
    for (var i = 0; i < segments.length; i++) {
      var seg = segments[i];
      if (seg.op === 'eq') {
        el.appendChild(document.createTextNode(seg.text));
      } else if (seg.op === keepOp) {
        var node = document.createElement(keepOp === 'del' ? 'del' : 'ins');
        node.className = keepOp === 'del' ? DIFF_DELETE_CLASSES : DIFF_INSERT_CLASSES;
        // The spoken prefix of pm/partials/message-body.twig.
        var label = document.createElement('bdi');
        label.className = 'sr-only';
        label.textContent = ' ' + (keepOp === 'del' ? labels.removed : labels.added) + ' ';
        node.appendChild(label);
        node.appendChild(document.createTextNode(seg.text));
        el.appendChild(node);
      }
    }
  }

  /** Open the correction modal for a message (called from the actions menu). */
  window.correctMessage = function (msgId) {
    window.closeMessageActionMenus();
    window.dispatchEvent(new CustomEvent('pm-correct-message', { detail: { msgId: String(msgId) } }));
  };

  /** Alpine component backing #correction-modal (pm/index.twig). */
  window.correctionModal = function () {
    return {
      open: false,
      msgId: '',
      original: '',
      text: '',
      comment: '',
      sending: false,
      error: '',
      compositionTracker: null,
      compositionSubmissionAttempt: null,

      init: function () {
        var self = this;
        this.$watch('text', function () {
          self.renderPreview();
        });
      },

      // Invisible characters do not count as a change and Unicode spaces read
      // the same as a plain space; mirrors TextDiff::isVisiblyEqual so the
      // server accepts whatever Send allows.
      get changed() {
        var norm = function (s) {
          return s
            // Stripping variation selectors / fillers is the intent here, so
            // the "misleading character class" they trigger is expected.
            // eslint-disable-next-line no-misleading-character-class
            .replace(/[\p{Cf}\u115F\u1160\u3164\uFFA0\u2800\u180B-\u180D\uFE00-\uFE0F]/gu, '')
            .replace(/[\u{E0100}-\u{E01EF}]/gu, '')
            .replace(/[\u00A0\u1680\u2000-\u200A\u2028\u2029\u202F\u205F\u3000]/g, ' ')
            .trim();
        };
        var trimmed = norm(this.text);
        return trimmed !== '' && trimmed !== norm(this.original);
      },

      openFromEvent: function (event) {
        this.openModal(event.detail && event.detail.msgId);
      },

      openModal: function (msgId) {
        if (!msgId) return;
        var msgEl = document.getElementById('msg_' + msgId);
        var raw = msgEl ? msgEl.getAttribute('data-msg-raw') : '';
        if (!raw) {
          // Stale DOM (message re-rendered between menu click and handler).
          console.warn('correction: no raw text for msg', msgId);
          return;
        }

        this.compositionTracker?.detach();
        this.msgId = String(msgId);
        this.original = raw;
        this.text = raw;
        this.comment = '';
        this.error = '';
        this.sending = false;
        this.open = true;
        this.compositionTracker = createTextCompositionTracker({
          initialValue: raw,
          baseline: 'preloaded_existing'
        });
        this.compositionSubmissionAttempt = null;

        var self = this;
        this.$nextTick(function () {
          if (self.$refs.editor) self.compositionTracker.attach(self.$refs.editor);
          self.renderPreview();
          if (window.LucideIcons) window.LucideIcons.initialize(self.$el);
          if (window.matchMedia('(min-width: 640px)').matches && self.$refs.editor) {
            self.$refs.editor.focus();
          }
        });
      },

      close: function () {
        if (this.sending) return;
        this.compositionTracker?.detach();
        this.compositionTracker?.discard(this.original, 'preloaded_existing');
        this.compositionSubmissionAttempt = null;
        this.open = false;
      },

      renderPreview: function () {
        if (!this.open || !this.changed) return;
        // The server trims the sent text before storing/diffing, so diff the
        // trimmed value or edge whitespace would preview as a phantom edit.
        var segments = window.pmTextDiff(this.original, this.text.trim());
        fillDiffLine(this.$refs.previewOriginal, segments, 'del', this.diffLabels());
        fillDiffLine(this.$refs.previewCorrected, segments, 'ins', this.diffLabels());
      },

      diffLabels: function () {
        return { removed: this.$root.dataset.diffRemoved || '', added: this.$root.dataset.diffAdded || '' };
      },

      submit: function () {
        if (!this.changed || this.sending) return;

        var threadId = window.get_active_thread_id();
        if (!$.isNumeric(threadId)) {
          this.error = window.tOr('common.msg_invalid_thread', 'Invalid conversation. Please refresh the page.');
          return;
        }

        this.sending = true;
        this.error = '';
        var self = this;
        var submittedText = this.text.trim();
        this.compositionSubmissionAttempt = this.compositionTracker?.freezeAttempt(
          submittedText,
          this.compositionSubmissionAttempt
        ) ?? null;
        var serializedComposition = this.compositionSubmissionAttempt?.serialized;

        window.pm_ajax_request_active = 1;
        $.ajax({
          type: 'POST',
          url: '/app/messages/send-message',
          data: {
            thread: threadId,
            message: submittedText,
            correct_msg_id: this.msgId,
            correction_comment: this.comment,
            ...(typeof serializedComposition === 'string'
              ? {text_composition: serializedComposition}
              : {})
          },
          dataType: 'json'
        }).done(function (response) {
          // Auth/CSRF/onboarding gate: the global ajaxComplete handler
          // navigates on {redirect}, so leave the modal untouched on the way out.
          if (response && typeof response.redirect === 'string' && response.redirect) {
            return;
          }
          if (response && response.success) {
            window.PmReplyOpeners?.markSent(threadId);
            if (response.body) {
              window.append_msgs(response.body, $('#threads_right'));
            }
            if (window.MessageDisplayManager) {
              window.MessageDisplayManager.updateThreadSidebar(response);
            }
            self.sending = false;
            self.compositionTracker?.detach();
            self.compositionTracker?.rebase(submittedText, 'preloaded_existing');
            self.compositionSubmissionAttempt = null;
            self.open = false;
          } else {
            self.sending = false;
            self.error = (response && response.error)
              || window.tOr('common.msg_send_failed', 'Error when sending message. Try again.');
          }
        }).fail(function () {
          self.sending = false;
          self.error = window.tOr('common.msg_network_error', 'Network error occurred. Please check your connection.');
        }).always(function () {
          window.pm_ajax_request_active = 0;
        });
      }
    };
  };
})();
